import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { sentryQueryKeys } from './queryKeys'
import { getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'

// Zod schema for issue details API response
const issueDetailsApiResponseSchema = z.object({
  success: z.boolean(),
  issueId: z.string(),
  url: z.string().optional().nullable(),
  issue: z.object({
    id: z.string(),
    title: z.string(),
    culprit: z.string(),
    status: z.string(),
    level: z.string(),
    type: z.string(),
    metadata: z.object({
      title: z.string().nullable().optional(),
      value: z.string().optional(),
      type: z.string().optional(),
      filename: z.string().optional(),
      function: z.string().optional(),
      in_app_frame_mix: z.string().optional(),
      sdk: z
        .object({
          name: z.string(),
          name_normalized: z.string(),
        })
        .optional(),
      initial_priority: z.number().optional(),
    }),
    numComments: z.number(),
    userCount: z.number(),
    count: z.union([z.string(), z.number()]),
    firstSeen: z.union([z.string(), z.date()]),
    lastSeen: z.union([z.string(), z.date()]),
    permalink: z.string(),
    shortId: z.string(),
    project: z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      platform: z.string(),
    }),
    assignedTo: z
      .object({
        id: z.string(),
        name: z.string(),
        email: z.string().optional(),
      })
      .nullable(),
    isBookmarked: z.boolean(),
    isSubscribed: z.boolean(),
    isUnhandled: z.boolean(),
    hasSeen: z.boolean(),
    annotations: z.array(z.unknown()),
  }),
  latestEvent: z.object({
    eventId: z.string(),
    dateCreated: z.union([z.string(), z.date()]).nullable().optional(),
    environment: z.string().nullable(),
    release: z.string().nullable(),
    transaction: z.string().nullable(),
    traceId: z.string().nullable(),
    browser: z
      .object({
        name: z.string().optional().nullable(),
        version: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
    os: z
      .object({
        name: z.string().optional().nullable(),
        version: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
    exception: z
      .object({
        type: z.string().optional().nullable(),
        value: z.string().optional().nullable(),
        mechanism: z
          .object({
            type: z.string().optional().nullable(),
            handled: z.boolean().optional().nullable(),
          })
          .optional()
          .nullable(),
      })
      .optional()
      .nullable(),
    stackTrace: z
      .array(
        z.object({
          filename: z.string().optional().nullable(),
          function: z.string().optional().nullable(),
          lineNo: z.number().optional().nullable(),
          colNo: z.number().optional().nullable(),
          context: z.record(z.unknown()).optional().nullable(),
          inApp: z.boolean().optional().nullable(),
        })
      )
      .optional()
      .nullable(),
    message: z.string().nullable().optional(),
    tags: z.array(
      z.object({
        key: z.string(),
        value: z.string(),
        query: z.string().optional(),
      })
    ),
    user: z
      .object({
        id: z.string().nullable(),
        email: z.string().nullable(),
        username: z.string().nullable(),
        ip_address: z.string().nullable(),
        name: z.string().nullable(),
        geo: z
          .object({
            country_code: z.string().optional().nullable(),
            city: z.string().optional().nullable(),
            region: z.string().optional().nullable(),
          })
          .nullable()
          .optional(),
        data: z.unknown().nullable(),
      })
      .optional()
      .nullable(),
    sdk: z
      .object({
        name: z.string(),
        version: z.string(),
      })
      .optional()
      .nullable(),
  }),
  trend: z.object({
    start: z.number(),
    end: z.number(),
    interval: z.number().nullable(),
    data: z.array(
      z.object({
        timestamp: z.number(),
        date: z.union([z.string(), z.date()]),
        count: z.array(
          z.object({
            count: z.number(),
          })
        ),
      })
    ),
  }),
  timestamp: z.union([z.string(), z.date()]),
})

export type IssueDetailsApiResponse = z.infer<typeof issueDetailsApiResponseSchema>

export const useIssueDetails = (issueId: string | null, workstream?: string | null) => {
  return useQuery({
    queryKey: issueId ? sentryQueryKeys.issueDetails(issueId) : ['issueDetails', null],
    queryFn: async () => {
      if (!issueId) {
        throw new Error('Issue ID is required')
      }

      // Build query params
      const queryParams = new URLSearchParams()

      // Add culprit query if workstream is provided (for Apex Client workstream filtering)
      if (workstream && workstream !== 'all') {
        const culpritQuery = getWorkstreamCulpritQuery(workstream)
        if (culpritQuery) {
          queryParams.append('query', culpritQuery)
        }
      }

      // Build the URL with query params if any
      const baseUrl = apiUrls.getIssueDetails(issueId)
      const urlWithParams = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl

      const { data } = await http.get(urlWithParams)

      // Validate response with Zod
      const validatedData = issueDetailsApiResponseSchema.parse(data)
      return validatedData
    },
    enabled: !!issueId,
    staleTime: 30000, // 30 seconds
    refetchOnMount: false,
  })
}
